/**
 * Profile Actions - JWT-based Backend
 * 
 * Manages merchant profile data with JWT authentication
 * NO BACKWARD COMPATIBILITY - Clean break from Basic Auth
 */

import * as actionType from "./actionTypes";
import apiClient from "../../services/apiClient";
import { API_ENDPOINTS } from "../../config/api.config";
import { showErrorToast, showSuccessToast } from "../../utils/errorHandler";

// ==============================
// ACTION CREATORS
// ==============================

export const getProfileDetailsStart = () => {
  return {
    type: actionType.PROFILE_GET_DETAILS_START,
  };
};

export const getProfileDetailsSuccess = (details) => {
  return {
    type: actionType.PROFILE_GET_DETAILS_SUCCESS,
    details,
  };
};

export const getProfileDetailsFail = () => {
  return {
    type: actionType.PROFILE_GET_DETAILS_FAIL,
  };
};

export const putProfileDetailsStart = () => {
  return {
    type: actionType.PROFILE_PUT_DETAILS_START,
  };
};

export const putProfileDetailsSuccess = (details) => {
  return {
    type: actionType.PROFILE_PUT_DETAILS_SUCCESS,
    details,
  };
};

export const putProfileDetailsFail = () => {
  return {
    type: actionType.PROFILE_PUT_DETAILS_FAIL,
  };
};

export const setNewObjectData = (name, value) => {
  return {
    type: actionType.PROFILE_SET_NEW_OBJ,
    name,
    value,
  };
};

// ==============================
// PROFILE OPERATIONS
// ==============================

/**
 * Get merchant profile details
 * Uses JWT authentication via apiClient
 */
export const getMerchantProfile = (merchantId) => {
  return async (dispatch) => {
    dispatch(getProfileDetailsStart());

    try {
      const response = await apiClient.get(API_ENDPOINTS.MERCHANTS_BY_ID(merchantId));
      
      // Backend returns merchant data directly or wrapped in { success, data }
      const merchantData = response.data.data || response.data;
      
      dispatch(getProfileDetailsSuccess(merchantData));
    } catch (error) {
      console.error('Get merchant profile error:', error);
      dispatch(getProfileDetailsFail());
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to load profile';
      showErrorToast({ message: errorMessage });
    }
  };
};

/**
 * Update merchant profile
 * Uses JWT authentication via apiClient
 */
export const updateMerchantProfile = (merchantId, profileData) => {
  return async (dispatch) => {
    dispatch(putProfileDetailsStart());

    try {
      const response = await apiClient.put(
        API_ENDPOINTS.MERCHANTS_BY_ID(merchantId),
        profileData
      );
      
      // Backend returns updated merchant data
      const merchantData = response.data.data || response.data;
      
      dispatch(putProfileDetailsSuccess(merchantData));
      showSuccessToast('Profile updated successfully!');
    } catch (error) {
      console.error('Update merchant profile error:', error);
      dispatch(putProfileDetailsFail());
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to update profile';
      showErrorToast({ message: errorMessage });
    }
  };
};

/**
 * Upload merchant logo
 * Uses FormData for file upload with JWT authentication
 */
export const uploadMerchantLogo = (merchantId, file) => {
  return async (dispatch) => {
    try {
      const formData = new FormData();
      formData.append('logo', file);

      const response = await apiClient.post(
        API_ENDPOINTS.MERCHANTS_LOGO_UPLOAD(merchantId),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Backend returns updated merchant data with new logo URL
      const merchantData = response.data.data || response.data;
      
      // Update profile with new logo
      dispatch(getProfileDetailsSuccess(merchantData));
      showSuccessToast('Logo uploaded successfully!');
      
      return merchantData.logoUrl;
    } catch (error) {
      console.error('Upload logo error:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to upload logo';
      showErrorToast({ message: errorMessage });
      
      throw error;
    }
  };
};

/**
 * Delete merchant logo
 */
export const deleteMerchantLogo = (merchantId) => {
  return async (dispatch) => {
    try {
      const response = await apiClient.delete(
        API_ENDPOINTS.MERCHANTS_LOGO_DELETE(merchantId)
      );

      // Backend returns updated merchant data
      const merchantData = response.data.data || response.data;
      
      dispatch(getProfileDetailsSuccess(merchantData));
      showSuccessToast('Logo deleted successfully!');
    } catch (error) {
      console.error('Delete logo error:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          'Failed to delete logo';
      showErrorToast({ message: errorMessage });
      
      throw error;
    }
  };
};

/**
 * Update profile object in state (for form editing)
 */
export const updateProfileObj = (name, value) => {
  return (dispatch) => {
    dispatch(setNewObjectData(name, value));
  };
};
